<template>
  <div>
    <v-row>
      <v-col>
        <v-toolbar flat>
          <v-toolbar-title class="capitalize">
            {{ $tc('common.department', 2, { n: '' }) }}
          </v-toolbar-title>
          <v-spacer />
          <v-text-field
            v-model="search"
            solo
            hide-details
            dense
            clearable
            class="mr-3"
            append-icon="mdi-magnify"
          />
          <v-spacer />
          <v-menu
            v-model="addMenu"
            left
            :close-on-content-click="false"
            min-width="500"
          >
            <template #activator="{ attrs, on }">
              <v-btn
                v-bind="attrs"
                v-on="on"
              >
                Ny {{ $tc('common.department', 1, { n: '' }) }}
                <v-icon right> mdi-plus </v-icon>
              </v-btn>
            </template>
            <v-card>
              <v-card-title>
                Ny {{ $tc('common.department', 1, { n: '' }) }}
              </v-card-title>
              <v-card-text>
                <v-form
                  ref="form"
                  v-model="addForm"
                >
                  <v-text-field
                    ref="newItemTitle"
                    v-model="newItem.title"
                    label="Navn"
                    autofocus
                    :rules="[ v => !!v || 'Udfyld et navn' ]"
                  />
                </v-form>
              </v-card-text>
              <v-card-actions>
                <v-btn
                  text
                  @click="clear(false); addMenu = false;"
                  small
                >
                  {{ $t('common.cancel') }}
                </v-btn>
                <v-btn text small @click="clear(true)">
                  Ryd
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn
                  :disabled="!addForm"
                  :loading="creating"
                  @click="create"
                  small
                >
                  Opret
                  <v-icon right> mdi-plus </v-icon>
                </v-btn>
                <v-btn
                  :disabled="!addForm"
                  :loading="creating"
                  @click="create(true)"
                  small
                >
                  Opret og ny
                  <v-icon right> mdi-plus </v-icon>
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>
        </v-toolbar>
        <v-data-table
          :items="items"
          :headers="headers"
          :loading="loading"
          :search="search"
          :items-per-page="15"
          sort-by="sortOrder"
          sort-desc
          :footer-props="{ 'items-per-page-options': [15, 30, 50, 75, -1] }"
        >
          <template #[`item.profitFactor`]="{ value }">
            {{ value * 100 }}%
          </template>
          <template #[`item.createdOn`]="{ value }">
            {{ value | formatDateTime }}
          </template>
          <template #[`item.actions`]="{ item }">
            <v-btn small @click="onEditItem(item)">
              <v-icon>mdi-pencil-outline</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <v-dialog
      v-model="editDialog"
      width="800"
    >
      <v-card>
        <v-card-title>
          Rediger
        </v-card-title>
        <v-card-text>
          <v-form
            ref="editForm"
            v-model="editForm"
          >
            <v-text-field
              v-model="editItem.title"
              label="Navn"
              autofocus
              outlined
              :rules="[ v => !!v || 'Udfyld et navn' ]"
            />
            <v-text-field
              v-model.number="editItem.sortOrder"
              label="Sortering"
              outlined
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-menu
            width="500"
            close-on-content-click="false"
          >
            <template #activator="{ attrs, on }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                color="error"
                text
              >
                Slet
                <v-icon right>
                  mdi-delete-outline
                </v-icon>
              </v-btn>
            </template>

            <v-card>
              <v-card-title>
                Bekræft sletning
              </v-card-title>
              <v-card-text>
                Er du sikker på du vil slette?
              </v-card-text>
              <v-card-actions>
                <v-btn text>
                  {{ $t('common.cancel') }}
                </v-btn>
                <v-spacer />
                <v-btn
                  :loading="deleting"
                  @click="deleteItem(editItem.id)"
                >
                  Ja, slet
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>
          <v-spacer />

          <v-btn :loading="saving" @click="saveItem()">
            {{ $t('common.save') }}
            <v-icon right> mdi-content-save-outline </v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
const baseUrl = 'departments';

export default {
  name: 'CustomerDepartments',
  props: ['customer'],
  data() {
    return {
      loading: true,
      creating: false,
      saving: false,
      deleting: false,
      addMenu: false,
      editDialog: false,
      editItem: {},
      addForm: false,
      editForm: false,
      newItem: {},
      items: [],
      headers: [
        { text: 'Navn', value: 'title', width: '30%' },
        { text: 'Sortering', value: 'sortOrder', align: 'right' },
        { text: 'Actions', value: 'actions', align: 'right', sortable: false }
      ],
      search: '',
    }
  },
  mounted: async function() {
    this.getItems();
  },
  watch: {
    editDialog(value) {
      if (!value) {
        this.editItem = {};
      }
    }
  },
  methods: {
    create: async function(leaveOpen = false) {
      this.creating = true;

      try {
        const model = {
          ...this.newItem,
          customerId: this.$route.params.customerId
        };

        const { data } = await this.$api.post(baseUrl, model);

        if (!data.success) throw data.errors?.at(0)?.message ?? data.message ?? 'Something went wrong';

        if (!leaveOpen || typeof leaveOpen !== 'boolean') {
          this.addMenu = false;
          this.clear(true);
        } else {
          this.clear(false);
          this.$refs.newItemTitle.focus();
        }

        this.$notifier.showSuccess({ message: `${this.$tc('common.department', 1, { n: '' })} oprettet` });
        this.getItems();
      } catch (error) {
        console.error(error);
        this.$notifier.showError({ message: error });
      } finally {
        this.creating = false;
      }
    },
    onEditItem: function(item) {
      this.editItem = JSON.parse(JSON.stringify(item));
      this.editDialog = true;
    },
    saveItem: async function() {
      this.saving = true;

      try {
        const { data } = await this.$api.patch(`${baseUrl}/${this.editItem.id}`, this.editItem);

        if (!data.success) throw data.errors?.at(0)?.message ?? data.message ?? 'Something went wrong';

        this.$notifier.showSuccess({ message: `${this.$tc('common.department', 1, { n: '' })} opdateret` });
        this.getItems();
        this.editDialog = false;
      } catch (error) {
        console.error(error);
        this.$notifier.showError({});
      } finally {
        this.saving = false;
      }
    },
    deleteItem: async function(id) {
      this.deleting = true;

      try {
        const { data } = await this.$api.delete(`${baseUrl}/${id}`);

        if (!data.success) throw data.errors?.at(0)?.message ?? data.message ?? 'Something went wrong';

        this.$notifier.showSuccess({ message: `${this.$tc('common.department', 1, { n: '' })} slettet` });
        this.getItems();
        this.editDialog = false;
      } catch (error) {
        console.error(error);
        this.$notifier.showError({});
      } finally {
        this.deleting = false;
      }
    },
    clear: function() {
      this.newItem = {};
      this.$refs.form.resetValidation();
    },
    getItems: async function() {
      this.loading = true;

      try {
        const { data } = await this.$api.get(baseUrl, {
          params: {
            customerId: this.$route.params.customerId
          }
        });

        this.items = data;
      } catch (error) {
        console.error(error);
        this.$notifier.showError({});
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>